import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
    deleteFile,
    downloadFile,
    getDownloadLink,
    getFileDetails,
    uploadFile,
} from '@/services/FileService';
import { errorSnackbar } from '@/utils/snackbar';

interface Validations {
    dimensions?: {
        min?: { height?: number; width?: number };
        max?: { height?: number; width?: number };
        aspectRatio?: number;
    };
    size?: number;
    acceptedTypes?: string[];
}

async function validateDimensions(file: File): Promise<boolean> {
    const objectUrl = URL.createObjectURL(file);

    try {
        const { width, height } = await new Promise<{ width: number; height: number }>(
            (resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve({ width: img.width, height: img.height });
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = objectUrl;
            },
        );

        // Dimension checks
        if (width > 400 || width < 200) return false;
        if (height > 400 || height < 200) return false;

        // Aspect ratio check
        if (width !== height) return false;

        return true;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

interface Props {
    lawFirmId?: number;
    fileId: string | undefined;
    validations?: Validations;
    onUploadSuccess?: (fileId: string) => void;
    onRemoveFile?: (fileId: string | undefined) => void;
}

export default function LogoImageUpload({
    lawFirmId,
    fileId,
    validations,
    onUploadSuccess,
    onRemoveFile,
}: Props) {
    const theme = useTheme();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [showError, setShowError] = useState<boolean>();
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const imageUrl = getDownloadLink(fileId);
    const [isImageLoading, setIsImageLoading] = useState(Boolean(imageUrl));
    const isBusy = isUploading || isDeleting || isImageLoading;

    useEffect(() => {
        if (!imageUrl) {
            setIsImageLoading(false);
            return;
        }

        setIsImageLoading(!imageRef.current?.complete);
    }, [imageUrl]);

    const onValidation = async (files: FileList | null) => {
        if (!files || files.length === 0) {
            errorSnackbar('No files selected');
            return false;
        }

        if (files.length > 1) {
            errorSnackbar('You can only upload one file at a time');
            return false;
        }

        const file = files[0];

        const acceptedFileTypes = validations?.acceptedTypes || [
            'image/jpeg',
            'image/jpg',
            'image/png',
        ];
        if (!acceptedFileTypes.includes(file.type)) {
            errorSnackbar(`Invalid file type. Only ${acceptedFileTypes.join(', ')} are accepted`);
            return false;
        }

        if (validations?.size && file.size > validations.size) {
            errorSnackbar(
                `File size is too large. Must be less than ${validations.size / (1024 * 1024)}MB`,
            );
            return false;
        }

        try {
            const isCorrectDimensions = await validateDimensions(file);
            if (!isCorrectDimensions) {
                errorSnackbar(
                    'Invalid dimensions. The dimensions should be between 200 and 400 pixels and the aspect ratio should be 1:1',
                );
                return false;
            }
        } catch (error) {
            console.error(error);
            errorSnackbar('Error validating image dimensions');
            return false;
        }

        return true;
    };

    const onFileUpload = async (files: FileList | null) => {
        if (isBusy) return;

        try {
            setIsUploading(true);
            const isValid = await onValidation(files);
            if (isValid && files) {
                setShowError(false);
                await handleFileOperations(files[0]);
            }
        } catch (error: any) {
            console.log(error);
            setShowError(true);
            errorSnackbar(error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleUploadImage = async (file: File, fileType: string) => {
        if (!lawFirmId) return;
        try {
            const response = await uploadFile(file, lawFirmId, fileType);
            onUploadSuccess?.(response.fileId);
        } catch (error: any) {
            errorSnackbar(error?.response?.data || 'Something went wrong');
            setShowError(true);
        }
    };

    const handleDelete = async () => {
        if (!fileId) return true;
        try {
            setIsDeleting(true);
            await deleteFile(fileId);
            return true;
        } catch (error: any) {
            errorSnackbar(error?.response?.data || 'Failed to delete logo!');
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async () => {
        if (!fileId || !imageUrl || isBusy) return;

        try {
            setIsDownloading(true);
            const fileDetails = await getFileDetails(fileId);
            const fileName = fileDetails?.data?.fileName ?? 'logo.png';
            const fileResponse = await downloadFile(fileId);
            const blobUrl = URL.createObjectURL(fileResponse.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            console.error('Error downloading logo:', error);
            errorSnackbar(error?.response?.data || 'Failed to download logo');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileOperations = async (fileObject: File) => {
        const imageType = fileObject.type.split('/')[1];
        if (fileId) {
            const didDelete = await handleDelete();
            if (!didDelete) return;
        }

        await handleUploadImage(fileObject, imageType);
    };

    return (
        <>
            {imageUrl ? (
                <Box
                    sx={{
                        height: '200px',
                        width: '200px',
                        borderRadius: '4px',
                        position: 'relative',
                    }}
                >
                    <img
                        ref={imageRef}
                        alt="Law firm logo"
                        src={imageUrl}
                        onLoad={() => setIsImageLoading(false)}
                        onError={() => setIsImageLoading(false)}
                        style={{
                            height: '200px',
                            width: '200px',
                            borderRadius: '4px',
                        }}
                    />
                    <Box
                        sx={{
                            height: '200px',
                            width: '200px',
                            borderRadius: '4px',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '24px',
                            backgroundColor: 'rgba(25, 25, 29, 0.50)',
                            opacity: isBusy ? 1 : 0,
                            '&:hover': {
                                opacity: 1,
                            },
                        }}
                    >
                        <input
                            type="file"
                            aria-label="Choose an image file"
                            accept="image/*"
                            multiple={false}
                            ref={fileInputRef}
                            style={{
                                display: 'none',
                            }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onFileUpload(event.target?.files);
                            }}
                            disabled={isBusy}
                        ></input>
                        {isBusy ? (
                            <CircularProgress
                                size={32}
                                sx={{ color: theme.palette.neutral.white }}
                            />
                        ) : (
                            <>
                                <Button
                                    sx={{
                                        minWidth: '24px',
                                        height: 'fit-content !important',
                                        padding: '0px',
                                        color: theme.palette.neutral.white,
                                    }}
                                    disabled={isDownloading}
                                    onClick={handleDownload}
                                >
                                    <DownloadIcon />
                                </Button>
                                <Button
                                    sx={{
                                        minWidth: '24px',
                                        height: 'fit-content !important',
                                        padding: '0px',
                                        color: theme.palette.neutral.white,
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </Button>
                                <Button
                                    sx={{
                                        minWidth: '24px',
                                        height: 'fit-content !important',
                                        padding: '0px',
                                        color: theme.palette.neutral.white,
                                    }}
                                    onClick={async () => {
                                        const didDelete = await handleDelete();
                                        if (didDelete) {
                                            onRemoveFile?.(undefined);
                                        }
                                    }}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            ) : (
                <Box
                    component="div"
                    role="region"
                    aria-label="Image upload"
                    onDrop={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (isBusy) return;
                        onFileUpload(event.dataTransfer?.files);
                    }}
                    onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    sx={{
                        height: '200px',
                        minWidth: '200px',
                        border: showError
                            ? `1px dashed ${theme.palette.error.main}`
                            : `1px dashed ${theme.palette.neutral.gray}`,
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {isBusy ? (
                        <CircularProgress size={32} />
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <CloudUploadIcon
                                    sx={{ fontSize: 16, color: theme.palette.neutral.gray }}
                                />
                                <Typography variant="body2">Drag & drop your logo</Typography>
                            </Box>
                            <Typography variant="body3">or</Typography>

                            <label
                                style={{
                                    display: 'inline-block',
                                    padding: '8px 16px',
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'rgba(54, 120, 215, 0.08)',
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    lineHeight: '16px',
                                    textAlign: 'center',
                                }}
                            >
                                <input
                                    type="file"
                                    aria-label="Choose an image file"
                                    accept="image/*"
                                    multiple={false}
                                    style={{
                                        display: 'none',
                                    }}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onFileUpload(event.target?.files);
                                    }}
                                    disabled={isBusy}
                                ></input>
                                Upload File
                            </label>
                            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                                400 x 400 px
                            </Typography>
                        </>
                    )}
                </Box>
            )}
        </>
    );
}
